/**
 * Abrechnungs-Detail — Positionen (Erlös/Kosten getrennt), Summen mit Steuer,
 * Finalisieren, Rechnungs-Nr./Notiz, CSV-Export (nur Erlös-Positionen).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { StatementDetail, deleteStatement, downloadCsv, extractApiError, finalizeStatement, getStatement, handoffStatement, updateStatement } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCents, formatPercent, periodLabel } from '../services/format';
import { BillingTabs } from '../components/billing/BillingTabs';
import { LineItemsTable } from '../components/billing/LineItemsTable';
import { STATEMENT_STATUS } from './BillingStatementsPage';

export function BillingStatementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [st, setSt] = useState<StatementDetail | null>(null);
  const [notes, setNotes] = useState('');
  const [extRef, setExtRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function apply(s: StatementDetail): void { setSt(s); setNotes(s.notes ?? ''); setExtRef(s.externalRef ?? ''); }
  useEffect(() => { if (id) getStatement(id).then(apply).catch((e) => setError(extractApiError(e))); }, [id]);

  async function act(fn: () => Promise<StatementDetail | void>): Promise<void> {
    setBusy(true); setError(null);
    try { const r = await fn(); if (r) apply(r); } catch (e) { setError(extractApiError(e)); } finally { setBusy(false); }
  }

  if (!st) return <div><BillingTabs />{error ? <p className="text-red-700">{error}</p> : <p className="text-slate-500">Lade…</p>}</div>;

  const revenue = st.lines.filter((l) => l.side === 'revenue');
  const cost = st.lines.filter((l) => l.side === 'cost');
  const status = STATEMENT_STATUS[st.status] ?? { label: st.status, cls: '' };

  return (
    <div>
      <BillingTabs />
      <Link to="/billing/statements" className="text-sm text-primary-600 hover:underline">← Abrechnungen</Link>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-1 mb-4">
        <div>
          <h2 className="text-2xl font-semibold">
            <Link to={`/customers/${st.customer.id}`} className="hover:underline">{st.customer.companyName}</Link> · {periodLabel(st.periodKey)}
          </h2>
          <p className="text-sm text-slate-500">
            <span className={`badge ${status.cls}`}>{status.label}</span>{' '}
            {st.customer.vatId && <span className="font-mono">USt-ID {st.customer.vatId} · </span>}
            {st.customer.country ?? '—'} · {st.customer.billingEmail ?? 'keine Rechnungs-E-Mail'} · Preisliste: {st.priceList?.name ?? '—'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="px-3 py-1 rounded border" disabled={busy} onClick={() => downloadCsv(`/billing/statements/${st.id}.csv`, `abrechnung-${st.customer.companyName}-${st.periodKey}.csv`).catch((e) => setError(extractApiError(e)))}>CSV</button>
          {st.status === 'draft' && <button type="button" className="btn-primary" disabled={busy || isReadOnly} onClick={() => { if (window.confirm('Abrechnung finalisieren? Sie wird bei künftigen Abrechnungsläufen nicht mehr neu berechnet.')) void act(() => finalizeStatement(st.id)); }}>Finalisieren</button>}
          {(st.status === 'finalized' || (st.status === 'exported' && !st.erpHandoffAt)) && <button type="button" className="btn-primary" disabled={busy || isReadOnly} onClick={() => { if (window.confirm('Erlös-Positionen an das ERP Intermal übergeben? Die Tagesabrechnung dort erzeugt daraus eine Rechnung; die Rechnungsnummer erscheint hier automatisch.')) void act(() => handoffStatement(st.id)); }}>An ERP übergeben</button>}
          {st.status === 'finalized' && <button type="button" className="btn-secondary" disabled={busy || isReadOnly} onClick={() => void act(() => updateStatement(st.id, { status: 'exported', externalRef: extRef || null }))}>Als exportiert markieren</button>}
          {st.status === 'finalized' && <button type="button" className="px-3 py-1 rounded border" disabled={busy || isReadOnly} onClick={() => void act(() => updateStatement(st.id, { status: 'draft' }))}>Wieder öffnen</button>}
          {st.status === 'draft' && <button type="button" className="btn-danger" disabled={busy || isReadOnly} onClick={() => { if (window.confirm('Entwurf löschen?')) void act(async () => { await deleteStatement(st.id); navigate('/billing/statements'); }); }}>Löschen</button>}
        </div>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <Stat label="Netto" value={formatCents(st.netCents)} />
        <Stat label={`MwSt ${(st.taxRateBp / 100).toLocaleString('de-DE')} %${st.reverseCharge ? ' (Reverse Charge)' : ''}`} value={formatCents(st.taxCents)} />
        <Stat label="Brutto" value={formatCents(st.grossCents)} strong />
        <Stat label="Selbstkosten (intern)" value={formatCents(st.costCents)} />
        <Stat label="Deckungsbeitrag" value={formatCents(st.marginCents)} tone={st.marginCents < 0 ? 'bad' : undefined} />
        <Stat label="Marge" value={formatPercent(st.netCents > 0 ? (st.marginCents / st.netCents) * 100 : null)} />
      </div>

      <div className="bg-white rounded shadow p-4 mb-4 grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Rechnungs-Nr. (ERP Intermal){st.erpHandoffAt && !st.externalRef && <> · übergeben {new Date(st.erpHandoffAt).toLocaleString('de-DE')}, Nummer ausstehend</>}{st.erpHandoffAt && st.externalRef && <> · übergeben {new Date(st.erpHandoffAt).toLocaleString('de-DE')}</>}</label>
          <input value={extRef} onChange={(e) => setExtRef(e.target.value)} disabled={isReadOnly} placeholder="INV-2026-00001" className="w-full px-2 py-1 border rounded font-mono" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Notiz (intern)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={isReadOnly} className="w-full px-2 py-1 border rounded" />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button type="button" className="px-3 py-1 rounded border" disabled={busy || isReadOnly || (notes === (st.notes ?? '') && extRef === (st.externalRef ?? ''))} onClick={() => void act(() => updateStatement(st.id, { notes: notes || null, externalRef: extRef || null }))}>Speichern</button>
        </div>
      </div>

      <h3 className="font-semibold mb-2">Erlös-Positionen ({revenue.length}) — Kundenrechnung</h3>
      <div className="bg-white rounded shadow mb-6 overflow-x-auto"><LineItemsTable lines={revenue} showTenant totalLabel="Netto" /></div>
      <h3 className="font-semibold mb-2">Selbstkosten ({cost.length}) — intern, nicht im Export</h3>
      <div className="bg-white rounded shadow mb-6 overflow-x-auto"><LineItemsTable lines={cost} showTenant totalLabel="Kosten" /></div>
      <p className="text-xs text-slate-400">Erstellt {new Date(st.createdAt).toLocaleString('de-DE')} von {st.createdBy}{st.finalizedAt && <> · finalisiert {new Date(st.finalizedAt).toLocaleString('de-DE')} von {st.finalizedBy}</>}.</p>
    </div>
  );
}

function Stat({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'bad' }) {
  return (
    <div className="bg-white rounded shadow p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`font-mono mt-1 ${strong ? 'text-lg font-semibold' : ''} ${tone === 'bad' ? 'text-red-700' : ''}`}>{value}</p>
    </div>
  );
}
